import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "@/components/ui/select";
import { Boxes, Upload, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * BU Stock Snapshots — procurement-owned monthly closing stock per BU.
 *
 * Backend contract:
 *   • POST /api/finance/stock-snapshots           — single JSON row
 *   • POST /api/finance/stock-snapshots/import    — XLSX bulk upload
 *   • GET  /api/finance/stock-snapshots?fy_year=  — list, optionally by FY
 *   • DELETE /api/finance/stock-snapshots/{id}    — remove one
 *
 * Once uploaded, snapshots automatically drive the Opening Stock and
 * Closing Stock columns of the P&L Summary. Opening = prior month's
 * closing, Closing = the value on this row. No sync required.
 */
export function BUStockSnapshotsPanel() {
  const [snapshots, setSnapshots] = useState([]);
  const [bus, setBus] = useState([]);
  const [fyFilter, setFyFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ bu_code: "", period_month: "", closing_stock: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = fyFilter ? { fy_year: fyFilter } : {};
      const [{ data }, buRes] = await Promise.all([
        api.get("/finance/stock-snapshots", { params }),
        api.get("/org/business-units"),
      ]);
      setSnapshots(data?.snapshots || []);
      setBus((buRes?.data || []).filter((b) => b.active));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fyFilter]);   // eslint-disable-line

  const uploadXlsx = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/finance/stock-snapshots/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errCount = (data?.errors || []).length;
      toast.success(`Inserted ${data.inserted}, updated ${data.updated}` +
                    (errCount ? ` (${errCount} row error${errCount === 1 ? "" : "s"})` : ""));
      if (errCount) console.warn("Stock upload row errors:", data.errors);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
  };

  const addOne = async () => {
    if (!form.bu_code || !form.period_month || !form.closing_stock) {
      toast.error("BU, month and closing stock are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/finance/stock-snapshots", {
        bu_code: form.bu_code,
        period_month: form.period_month,
        closing_stock: parseFloat(form.closing_stock),
        notes: form.notes || null,
      });
      toast.success("Saved");
      setForm({ bu_code: "", period_month: "", closing_stock: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setBusy(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await api.delete(`/finance/stock-snapshots/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Boxes className="w-4 h-4 text-slate-600" />
            <div>
              <div className="font-medium text-slate-800">BU Stock Snapshots</div>
              <div className="text-xs text-slate-500">
                Monthly closing stock per Business Unit. Opening = prior month's closing (auto).
                Feeds into the P&L Summary as stock movement inside COGS.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={fyFilter || "__all__"}
                    onValueChange={(v) => setFyFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-28 text-xs" data-testid="stock-fy-filter">
                <SelectValue placeholder="All FYs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All FYs</SelectItem>
                <SelectItem value="2026">FY26</SelectItem>
                <SelectItem value="2027">FY27</SelectItem>
                <SelectItem value="2028">FY28</SelectItem>
              </SelectContent>
            </Select>
            <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer"
                   data-testid="stock-upload-xlsx">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                      <Upload className="w-3.5 h-3.5" />}
              Upload XLSX
              <input type="file" accept=".xlsx,.xls" className="hidden"
                     onChange={(e) => uploadXlsx(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        {/* Manual entry row */}
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/40 grid grid-cols-12 gap-2 items-end">
          <div className="col-span-3">
            <Label className="text-[11px] text-slate-500">BU</Label>
            <Select value={form.bu_code || undefined}
                    onValueChange={(v) => setForm({ ...form, bu_code: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="stock-form-bu">
                <SelectValue placeholder="Select BU" />
              </SelectTrigger>
              <SelectContent>
                {bus.map((b) => (
                  <SelectItem key={b.code} value={b.code}>
                    {b.code} — {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-slate-500">Month (YYYY-MM)</Label>
            <Input value={form.period_month}
                   onChange={(e) => setForm({ ...form, period_month: e.target.value })}
                   placeholder="2026-07"
                   className="h-8 text-xs"
                   data-testid="stock-form-month" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px] text-slate-500">Closing Stock ($)</Label>
            <Input value={form.closing_stock}
                   onChange={(e) => setForm({ ...form, closing_stock: e.target.value })}
                   type="number" step="0.01" placeholder="985432.10"
                   className="h-8 text-xs"
                   data-testid="stock-form-value" />
          </div>
          <div className="col-span-3">
            <Label className="text-[11px] text-slate-500">Notes (optional)</Label>
            <Input value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className="h-8 text-xs"
                   data-testid="stock-form-notes" />
          </div>
          <div className="col-span-2">
            <Button size="sm" onClick={addOne} disabled={busy}
                    className="h-8 text-xs w-full"
                    data-testid="stock-form-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>

        {/* Existing snapshots table */}
        <div className="text-xs">
          <div className="grid grid-cols-12 gap-2 px-5 py-2 text-slate-500 border-b bg-slate-50/60 font-medium">
            <div className="col-span-2">BU</div>
            <div className="col-span-2">Month</div>
            <div className="col-span-2 text-right">Closing Stock</div>
            <div className="col-span-3">Notes</div>
            <div className="col-span-2">Uploaded by</div>
            <div className="col-span-1"></div>
          </div>
          {loading && <div className="px-5 py-4 text-slate-500">Loading…</div>}
          {!loading && snapshots.length === 0 && (
            <div className="px-5 py-6 text-slate-500 text-center">
              No snapshots yet. Upload an XLSX or add rows manually above —
              your procurement team's stocktake spreadsheet is a fine template
              (columns: BU Code, Period Month, Closing Stock, Notes).
            </div>
          )}
          {!loading && snapshots.map((s) => (
            <div key={s._id}
                 className="grid grid-cols-12 gap-2 px-5 py-2 border-b border-slate-100 hover:bg-slate-50/40 items-center">
              <div className="col-span-2 font-mono text-slate-700">{s.bu_code}</div>
              <div className="col-span-2 font-mono text-slate-600">{s.period_month}</div>
              <div className="col-span-2 text-right tabular-nums text-slate-800">
                ${(s.closing_stock || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="col-span-3 text-slate-500 truncate" title={s.notes || ""}>
                {s.notes || "—"}
              </div>
              <div className="col-span-2 text-slate-500 truncate" title={s.uploaded_by}>
                {s.uploaded_by || "—"}
              </div>
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600"
                        onClick={() => del(s._id)}
                        data-testid={`stock-del-${s.bu_code}-${s.period_month}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
